import React from "react";
import ReactDOM from "react-dom"
import Home from "./Home.jsx";
import './components/ui/universe/style';
import 'tdesign-react/es/style/index.css'; // 少量公共样式
import "./index.css";
import { MathJaxContext } from 'better-react-mathjax';

// 版本信息 (由 webpack DefinePlugin 注入)
console.log(`%c OCR Demo ${process.env.APP_VERSION} %c Build: ${process.env.BUILD_TIME} `, 
  'background: #1890ff; color: #fff; padding: 2px 6px; border-radius: 3px 0 0 3px;',
  'background: #52c41a; color: #fff; padding: 2px 6px; border-radius: 0 3px 3px 0;'
);

// 全局捕获 MathJax 相关的未处理错误，避免控制台报错
window.addEventListener('error', (event) => {
  if (event.message?.includes('Typesetting failed') ||
      event.message?.includes('nextSibling') ||
      event.message?.includes('contains')) {
    console.warn('[MathJax] Suppressed typesetting error:', event.message);
    event.preventDefault();
    return true;
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.message?.includes('Typesetting failed') ||
      event.reason?.message?.includes('nextSibling') ||
      event.reason?.message?.includes('contains')) {
    console.warn('[MathJax] Suppressed typesetting rejection:', event.reason?.message);
    event.preventDefault();
    return true;
  }
});

/**
 * 打印 MathJax typeset 失败时的现场信息
 * @param {string} fnName - 失败的方法名
 * @param {*} nodes - 传入的待排版节点
 * @param {*} err - 原始错误
 */
const logMathJaxFailure = (fnName, nodes, err) => {
  const contents = (Array.isArray(nodes) ? nodes : [nodes])
    .filter(Boolean)
    .map((n) => (typeof n === 'string' ? n : n.textContent) || '')
    .join(' || ');
  console.warn(
    `[MathJax] ${fnName} 失败，已降级不阻断渲染\n错误: ${(err && err.stack) || err}\n触发内容: ${contents}`
  );
};

/**
 * 过滤掉入参数组里已被卸载的节点（null）。
 *
 * 注意：MathJax 约定「不传 / 传 null / 传 undefined」表示对整页排版，
 * 所以只处理数组入参，不改变非数组入参的语义。
 *
 * @param {*} nodes - typeset 入参
 * @returns {*} 过滤后的入参
 */
const filterDetachedNodes = (nodes) => {
  if (!Array.isArray(nodes)) return nodes;
  return nodes.filter(Boolean);
};

/**
 * 修复 better-react-mathjax 在异步回调里读取已卸载 ref 导致的 MathJax 崩溃。
 *
 * 根因：better-react-mathjax 的 MathJax 组件里
 *   useLayoutEffect(() => {
 *     if ((dynamic || !mountedRef.current) && null !== spanRef.current) {
 *       ... MathJax 加载 Promise.then(() =>
 *             (MathJax.typesetClear([spanRef.current]),
 *              MathJax.typesetPromise([spanRef.current])))   // ← 此处 spanRef.current 已可能为 null
 *     }
 *   })
 * 只在同步的 effect 里判空，异步回调里又重新读取了 ref：组件在 MathJax 加载完成前
 * 被卸载（切换菜单、结果刷新等）时，ref 已被置为 null，于是实际调用变成
 * typesetPromise([null])，MathJax 内部遍历节点时空指针，抛
 * "Cannot read properties of null (reading 'nextSibling')"，
 * 再被包装成 "Typesetting failed" 变成未捕获错误。
 * 首屏 MathJax 还在从 CDN 加载时，一批公式组件会一起排队，加载完成后集体执行，
 * 因此表现为一次性刷出多条同样的报错。
 *
 * 这里在调用前剔除 null 节点（全部为 null 时直接跳过），并对残余异常兜底打印，
 * 含原始错误栈与触发内容原文，便于后续定位。
 *
 * @returns {boolean} 是否已成功打补丁
 */
const patchMathJaxTypesetErrors = () => {
  const mj = window.MathJax;
  if (!mj || typeof mj.typesetPromise !== 'function') return false;
  if (mj.__ocrDemoTypesetPatched) return true;

  const wrap = (fnName, { skipWhenEmpty = false } = {}) => {
    const original = mj[fnName];
    if (typeof original !== 'function') return;
    mj[fnName] = function (...args) {
      const nodes = filterDetachedNodes(args[0]);
      // 节点已全部卸载，无需排版，也不再交给 MathJax
      if (skipWhenEmpty && Array.isArray(nodes) && nodes.length === 0) {
        return Promise.resolve();
      }
      try {
        const result = original.call(mj, nodes, ...args.slice(1));
        if (result && typeof result.catch === 'function') {
          return result.catch((err) => logMathJaxFailure(fnName, args[0], err));
        }
        return result;
      } catch (err) {
        logMathJaxFailure(fnName, args[0], err);
      }
    };
  };

  wrap('typesetPromise', { skipWhenEmpty: true });
  wrap('typesetClear');
  mj.__ocrDemoTypesetPatched = true;
  return true;
};

// typeset 相关方法要等 MathJax 组件全部加载完成后才会挂到全局，因此轮询等待
const setupMathJaxTypesetGuard = (retry = 0) => {
  if (patchMathJaxTypesetErrors()) return;
  if (retry < 100) {
    setTimeout(() => setupMathJaxTypesetGuard(retry + 1), 100);
  }
};

// 全局 MathJax 配置
const mathJaxConfig = {
  loader: {
    load: [
      "[tex]/html",
      "[tex]/mhchem",     // 化学公式支持 \ce{}
      "[tex]/extpfeil",   // 长等号/箭头支持 \xlongequal{}, \xrightarrow{}
      "[tex]/ams",        // AMS 数学扩展，支持 \stackrel{} 等
      "[tex]/cases"       // cases 环境支持
    ]
  },
  tex: {
    packages: { "[+]": ["html", "mhchem", "extpfeil", "ams", "cases"] },
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"]
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"]
    ]
  },
  options: {
    // 关闭无障碍语义增强（SRE）。
    // MathJax 3.2 默认开启 enableEnrichment / enableAssistiveMml，其语义树构建与
    // 高亮标注代码会直接解引用节点（如 this.ofenceMml.nextSibling、t.node.nextSibling），
    // 在 React 频繁重渲染/卸载的场景下节点为 null，抛
    // "Cannot read properties of null (reading 'nextSibling')"，
    // 被 better-react-mathjax 包装成 "Typesetting failed" 上报。
    // 这些能力（屏幕阅读器语义树、盲文、公式阅读器、右键菜单）对 Demo 无实际价值，
    // 关闭后公式渲染不受影响，且可绕开上述代码路径。
    enableMenu: false,
    enableAssistiveMml: false,
    enableEnrichment: false,
    enableBraille: false,
    enableComplexity: false,
    enableExplorer: false,
  },
  startup: {
    typeset: false // 禁用自动 typeset，让 React 组件控制
  }
};

import {
  BrowserRouter as Router,
  Route
} from 'react-router-dom';
export default function App() {
  return (
    <MathJaxContext
      version={3}
      config={mathJaxConfig}
      onStartup={() => setupMathJaxTypesetGuard()}
    >
      <Router>
        {/* <Route path="/" component={Home} /> */}
        <Route path="*" component={Home} />
      </Router>
    </MathJaxContext>
  )
}

const root = document.getElementById("root")
ReactDOM.render(<App />, root)

